import { SendCondition } from '@spike/form-model';
import { v4 as uuid } from 'uuid';

export type FormType = 'intake' | 'other';
export type FormStatus = 'pending' | 'completed';

export const FORM_SEND_CONDITION_COPY: Record<string, { title: string; description: string }> = {
    [SendCondition.FirstBooking]: {
        title: 'First Booking',
        description: 'Only send to the client on their first visit.'
    },
    [SendCondition.EveryBooking]: {
        title: 'Every Booking',
        description: 'Send to the client on every booking.'
    },
    [SendCondition.Manually]: {
        title: 'Manually',
        description: 'Send to the client as needed.'
    }
};

export const isAutomaticSendCondition = (sendCondition?: string): boolean =>
    sendCondition === SendCondition.FirstBooking || sendCondition === SendCondition.EveryBooking;

export const getFormSendConditionDescription = (sendCondition?: string): string => {
    if (!sendCondition) return '';
    return FORM_SEND_CONDITION_COPY[sendCondition]?.description ?? '';
};

export interface AssociatedForm {
    id: number;
    title: string;
    sendCondition: string;
    active?: boolean;
}

export interface FormField {
    id: string;
    type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'date' | 'number';
    label: string;
    required: boolean;
    answerType: 'short_answer' | 'long_answer' | 'single_choice' | 'multiple_choice';
    displayOrder?: number;
    category?: 'for_pets' | 'for_customers';
    options?: FieldOption[];
    placeholder?: string;
    validation?: {
        min?: number;
        max?: number;
        pattern?: string;
    };
    _destroy?: boolean;
}

export interface FieldOption {
    id?: string | number;
    label: string;
    position: number;
    active?: boolean;
    value?: string;
    tempId?: string;
    _destroy?: boolean;
}

export interface FormResponse {
    id: string;
    formId: number;
    clientId?: number;
    petId?: number;
    responses: Record<string, any>;
    submittedAt: Date;
    status: FormStatus;
}

export interface Form {
    id?: number;
    uuid: string;
    title: string;
    description?: string;
    type: FormType;
    sentOnlyCount?: number;
    notCompletedCount?: number;
    fields: FormField[];
    active: boolean;
    required: boolean;
    createdAt: Date;
    updatedAt: Date;
    marketplaceId: number;
    generatedLink?: string; // Non-editable, automatically generated
    responses?: FormResponse[];
    sendCondition?: SendCondition | string;
    serviceIds?: number[];
    services?: Array<{
        id: number;
        name?: string;
        businessArea?: string;
    }>;
}

export const createEmptyForm = (
    marketplaceId: number,
    type: FormType = 'other',
    options?: { sendCondition?: SendCondition; serviceIds?: number[] }
): Form => ({
    uuid: uuid(),
    title: '',
    description: '',
    type,
    fields: [],
    active: true,
    required: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    marketplaceId,
    sendCondition: options?.sendCondition ?? SendCondition.Manually,
    serviceIds: options?.serviceIds ?? []
});

export const createIntakeForm = (marketplaceId: number): Form => ({
    uuid: uuid(),
    title: 'Client Intake Form',
    description: 'Standard intake form for new client onboarding',
    type: 'intake',
    fields: [
        {
            id: 'client_name',
            type: 'text',
            label: 'Client Name',
            required: true,
            placeholder: 'Enter your full name'
        },
        {
            id: 'phone',
            type: 'text',
            label: 'Phone Number',
            required: true,
            placeholder: '(555) 123-4567'
        },
        {
            id: 'email',
            type: 'text',
            label: 'Email Address',
            required: true,
            placeholder: 'your@email.com'
        },
        {
            id: 'pet_name',
            type: 'text',
            label: 'Pet Name',
            required: true,
            placeholder: "Enter your pet's name"
        },
        {
            id: 'pet_type',
            type: 'select',
            label: 'Pet Type',
            required: true,
            options: ['Dog', 'Cat', 'Bird', 'Other']
        },
        {
            id: 'emergency_contact',
            type: 'textarea',
            label: 'Emergency Contact Information',
            required: false,
            placeholder: 'Name and phone number of emergency contact'
        }
    ],
    active: true,
    required: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    marketplaceId
});
